/**
 * Componente reutilizável para renderizar campos de especificação por categoria
 * Usado em: Novo Orçamento, Novo Pedido, Catálogo, Estoque
 */

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getCategoryFieldConfig, isCurtainCategory } from "@/utils/category-fields-config";
import { CurtainType, getCurtainTypes } from "@/integrations/supabase/taxonomies";
import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";

interface SpecificationFieldsProps {
  categoryName: string;
  onSpecificationsChange?: (specs: Record<string, any>) => void;
  initialValues?: Record<string, any>;
  readOnly?: boolean;
}

export function SpecificationFields({
  categoryName,
  onSpecificationsChange,
  initialValues = {},
  readOnly = false,
}: SpecificationFieldsProps) {
  const { empresa } = useAuth();
  const [specifications, setSpecifications] = useState<Record<string, any>>(initialValues);

  // Buscar tipos de cortina se for categoria cortina
  const { data: curtainTypes = [] } = useQuery({
    queryKey: ["curtain-types", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id || !isCurtainCategory(categoryName)) return [];
      return await getCurtainTypes(empresa.id);
    },
    enabled: !!empresa?.id && isCurtainCategory(categoryName),
  });

  // Obter configuração de campos para a categoria
  const fieldConfig = getCategoryFieldConfig(categoryName);

  // Se não houver campos especiais, não renderizar nada
  if (!fieldConfig || fieldConfig.fields.length === 0) {
    return null;
  }

  // Atualizar pai quando especificações mudam
  useEffect(() => {
    onSpecificationsChange?.(specifications);
  }, [specifications, onSpecificationsChange]);

  const handleFieldChange = (key: string, value: any) => {
    setSpecifications((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          ✨ Especificações de {fieldConfig.category}
        </CardTitle>
        <CardDescription className="text-xs">
          {fieldConfig.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fieldConfig.fields.map((field) => {
          // Pular campos especiais como "nenhum"
          if (field.key === "nenhum" || field.key === "medidas") {
            return null;
          }

          const value = specifications[field.key] ?? "";

          // Renderizar por tipo de campo
          if (field.type === "select" && field.key === "tipoCortinaId") {
            const isCustomType = String(value).startsWith("custom:");
            const customValue = isCustomType ? String(value).replace("custom:", "") : "";
            const selectValue = isCustomType ? "custom" : (value || "");

            return (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="text-sm font-medium">
                  {field.label}
                  {!field.required && <span className="text-gray-400 text-xs ml-1">(opcional)</span>}
                </Label>
                <div className="space-y-2">
                  <Select
                    value={selectValue}
                    onValueChange={(val) => {
                      if (val === "custom") {
                        handleFieldChange(field.key, "custom:");
                      } else {
                        handleFieldChange(field.key, val);
                      }
                    }}
                    disabled={readOnly}
                  >
                    <SelectTrigger id={field.key} className="bg-white">
                      <SelectValue placeholder={field.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {curtainTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">+ Criar novo tipo</SelectItem>
                    </SelectContent>
                  </Select>
                  {isCustomType && (
                    <Input
                      type="text"
                      placeholder="Digite o nome do tipo de cortina..."
                      value={customValue}
                      onChange={(e) => handleFieldChange(field.key, "custom:" + e.target.value)}
                      disabled={readOnly}
                      className="bg-white border-blue-300"
                      autoFocus
                    />
                  )}
                </div>
              </div>
            );
          }

          if (field.type === "number") {
            return (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="text-sm font-medium">
                  {field.label} {field.unit && `(${field.unit})`}
                  {!field.required && <span className="text-gray-400 text-xs ml-1">(opcional)</span>}
                </Label>
                <Input
                  id={field.key}
                  type="number"
                  placeholder={field.placeholder}
                  value={value}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  disabled={readOnly}
                  className="bg-white"
                />
              </div>
            );
          }

          if (field.type === "textarea") {
            return (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="text-sm font-medium">
                  {field.label}
                  {!field.required && <span className="text-gray-400 text-xs ml-1">(opcional)</span>}
                </Label>
                <Textarea
                  id={field.key}
                  placeholder={field.placeholder}
                  value={value}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  disabled={readOnly}
                  className="bg-white resize-none h-20"
                />
              </div>
            );
          }

          // Padrão: text
          return (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={field.key} className="text-sm font-medium">
                {field.label}
                {!field.required && <span className="text-gray-400 text-xs ml-1">(opcional)</span>}
              </Label>
              <Input
                id={field.key}
                type="text"
                placeholder={field.placeholder}
                value={value}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                disabled={readOnly}
                className="bg-white"
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * Componente para VISUALIZAR especificações (modo read-only, formatado)
 */
interface SpecificationDisplayProps {
  categoryName: string;
  specifications: Record<string, any>;
}

export function SpecificationDisplay({
  categoryName,
  specifications,
}: SpecificationDisplayProps) {
  const fieldConfig = getCategoryFieldConfig(categoryName);

  if (!fieldConfig || !specifications || Object.keys(specifications).length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
      <h4 className="font-semibold text-sm flex items-center gap-2">
        ✨ Especificações de {fieldConfig.category}
      </h4>
      <div className="space-y-1 text-sm">
        {fieldConfig.fields.map((field) => {
          if (field.key === "nenhum" || field.key === "medidas") return null;

          const value = specifications[field.key];
          if (value === null || value === undefined || value === "") return null;

          return (
            <div key={field.key} className="flex gap-2">
              <span className="font-medium">{field.label}:</span>
              <span>{String(value)} {field.unit ?? ""}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
